import { Box, Typography, useMediaQuery } from '@mui/material'
import ReportIcon from '@mui/icons-material/Report'
import Input from 'components/Input/Input'
import Modal from 'components/Modal/Modal'
import { Formik, Form } from 'formik'
import {
  useGetDeliveryMethods,
  usePostDeliveryAreaGroup,
  usePostDeliveryMethod,
} from 'hooks/useServiceAreas'
import { Dispatch, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import theme from 'lib/theme'
import { useAlert } from 'providers/AlertProvider'
import { useServiceAreas } from 'providers/ServiceAreasProvider'
import { DeliveryMethod } from 'types/api/serviceAreas'
import { createDeliveryMethodPayload } from './utils'

const initialValues = {
  areaGroupName: '',
}

type NewGroupModalProps = {
  placeId: string
  setModal: Dispatch<SetStateAction<{ right: boolean }>>
  modal: { right: boolean }
}

export const NewGroupModal = ({
  placeId,
  setModal,
  modal,
}: NewGroupModalProps) => {
  const { t } = useTranslation()
  const { setAlert } = useAlert()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { currencyCode } = useServiceAreas()

  const { mutateAsync, isLoading: isLoadingPostRequest } =
    usePostDeliveryAreaGroup(placeId as string)
  const { hasHomeDeliveryMethod } = useGetDeliveryMethods(placeId)
  const { mutateAsync: createDeliveryMethod } = usePostDeliveryMethod(
    placeId as string
  )

  const createDeliveryAreaGroup = async (values: { areaGroupName: string }) => {
    const modifiedValues = { name: values.areaGroupName }
    await mutateAsync(modifiedValues, {
      onSuccess: () => {
        setAlert(t('saved'), 'success', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        })
        setModal({ right: false })
      },
      onError: () => {
        setAlert(t('failureMessage'), 'failure', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'right',
        })
      },
    })
  }

  const handleSubmit = async (values: { areaGroupName: string }) => {
    if (hasHomeDeliveryMethod) {
      await createDeliveryAreaGroup(values)
    } else {
      const payload: DeliveryMethod = createDeliveryMethodPayload(
        'homedelivery',
        currencyCode
      )
      await createDeliveryMethod(payload, {
        onSuccess: () => {
          createDeliveryAreaGroup(values)
        },
        onError: () => {
          setAlert(t('failureMessage'), 'failure', {
            vertical: 'bottom',
            horizontal: isMobile ? 'center' : 'right',
          })
        },
      })
    }
  }

  const handleCloseModal = () => {
    setModal({ right: false })
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={async (values) => handleSubmit(values)}>
      {({ isValid, dirty, submitForm }) => (
        <Modal
          showButton
          buttonIsDisabled={!isValid || !dirty}
          isLoadingRequest={isLoadingPostRequest}
          onButtonClick={submitForm}
          buttonText={t('save')}
          setModal={handleCloseModal}
          modal={modal}
          title={t('serviceArea.newDeliveryAreaGroup')}
          subtitle={t('serviceArea.newDeliveryAreaGroup-instructions')}>
          <Form>
            <Box
              height='calc(100vh - 230px)'
              display='flex'
              flexDirection='column'
              justifyContent='space-between'>
              <Input
                autoFocus
                errorMessage=''
                label={t('name')}
                name='areaGroupName'
                type='text'
                variant='filled'
                fullWidth
                width='440px'
              />
              <Box display='flex'>
                <ReportIcon
                  sx={{
                    fill: theme.palette.error.main,
                    '&:hover': { fill: theme.palette.error.main },
                  }}
                />
                <Typography ml={1}>
                  {t('serviceArea.newGroupWarning')}
                </Typography>
              </Box>
            </Box>
          </Form>
        </Modal>
      )}
    </Formik>
  )
}
